import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import PermissionCheck from '@/components/common/PermissionCheck';
import Card from '@/components/ui/cards/Card';
import CardBody from '@/components/ui/cards/CardBody';
import CardHeader from '@/components/ui/cards/CardHeader';
import validationErrors from '@/lib/validationErrors';
import {
  getSettingService,
  getShimbirrPairingBundleService,
  updateSettingService,
} from '@/services/SettingServices';
import { useDispatch } from 'react-redux';

const FIELD_LIST = [
  'shimbirr_auth_enabled',
  'shimbirr_auth_mode',
  'shimbirr_portal_label',
  'shimbirr_auth_client_id',
  'shimbirr_auth_scope_bundle',
  'shimbirr_auth_require_business_profile',
  'shimbirr_auth_allow_otp_login',
  'shimbirr_pair_code',
  'shimbirr_auth_note',
];

const DEFAULTS = {
  shimbirr_auth_enabled: '0',
  shimbirr_auth_mode: 'hybrid',
  shimbirr_portal_label: 'Taban Food',
  shimbirr_auth_client_id: 'taban-food-suite',
  shimbirr_auth_scope_bundle:
    'profile business_profile connected_apps software_otp notifications',
  shimbirr_auth_require_business_profile: '1',
  shimbirr_auth_allow_otp_login: '1',
  shimbirr_pair_code: 'TFOOD-MAIN',
  shimbirr_auth_note:
    'Allow Shimbirr profile login, business sharing controls, and QR pairing for Taban Food.',
};

const THEME = {
  primary: '#156372',
  teal: '#167E7E',
  ink: '#0b3a3f',
};

const QR_CARD_SIZE = 300;
const QR_DIALOG_SIZE = 420;

const createPairingCode = () =>
  `TFOOD-${Date.now().toString(36).toUpperCase()}`;

const buildFormState = (data = {}) => {
  const nextForm = {
    ...DEFAULTS,
    ...(data || {}),
  };

  Object.keys(nextForm).forEach((key) => {
    if (nextForm[key] === '' || nextForm[key] == null) {
      nextForm[key] = DEFAULTS[key];
    }
  });

  return nextForm;
};

const formatInstant = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Not synced yet';

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleString();
};

const StatusPill = ({ tone, text }) => (
  <span
    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
      tone === 'emerald'
        ? 'bg-[#d7e7e9] text-[#156372]'
        : 'bg-[#fff1c8] text-[#8f6200]'
    }`}
  >
    {text}
  </span>
);

const MetricTile = ({ label, value }) => (
  <div className="rounded-2xl border border-white/12 bg-white/10 px-4 py-4 text-white">
    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/65">
      {label}
    </p>
    <p className="mt-2 text-base font-semibold">{value}</p>
  </div>
);

const ScanStep = ({ number, text }) => (
  <div className="flex items-start gap-3 rounded-2xl border border-[#156372]/10 bg-white px-4 py-4">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#156372] text-sm font-semibold text-white">
      {number}
    </div>
    <p className="text-sm leading-6 text-slate-600">{text}</p>
  </div>
);

const ExpandedQrModal = ({ isOpen, onClose, qrText, portalLabel, expiresAt }) => {
  if (!isOpen || !qrText) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/75 p-4">
      <button
        type="button"
        aria-label="Close QR preview"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div className="relative z-[1201] w-full max-w-xl rounded-[32px] bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#156372]/75">
              Large Scan View
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
              {portalLabel || 'Taban Food'}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Hold the phone steady and scan from the Shimbirr app.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-xl text-slate-500 transition hover:bg-slate-100"
          >
            ×
          </button>
        </div>

        <div className="mt-6 rounded-[32px] border border-slate-200 bg-white p-5 shadow-inner sm:p-7">
          <div className="rounded-[28px] border border-[#e7f2f4] bg-white p-5">
            <QRCode
              value={qrText}
              size={QR_DIALOG_SIZE}
              bgColor="#FFFFFF"
              fgColor="#000000"
              style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            />
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-2 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <span>Best result: keep 15-25 cm away and fill most of the camera frame.</span>
          <span>Expires: {formatInstant(expiresAt)}</span>
        </div>
      </div>
    </div>
  );
};

const ShimbirrAuthSettingsPage = ({ pageTitle }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(DEFAULTS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pairingBundle, setPairingBundle] = useState(null);
  const [pairingGate, setPairingGate] = useState(null);
  const [isQrOpen, setIsQrOpen] = useState(false);

  const shimbirrEnabled = String(form.shimbirr_auth_enabled || '0') === '1';
  const qrReady = shimbirrEnabled && Boolean(pairingBundle?.qrText);
  const statusTone = qrReady ? 'emerald' : 'amber';
  const statusText = !shimbirrEnabled
    ? 'Disconnected'
    : qrReady
      ? 'QR Ready'
      : 'Preparing QR';

  useEffect(() => {
    if (!qrReady) {
      setIsQrOpen(false);
    }
  }, [qrReady]);

  const fetchPairingBundle = async (shouldFetch = shimbirrEnabled) => {
    if (!shouldFetch) {
      setPairingBundle(null);
      setPairingGate(null);
      return;
    }

    const response = await dispatch(getShimbirrPairingBundleService());
    setPairingBundle(response?.data?.data?.securePairing || null);
    setPairingGate(response?.data?.data?.pairingGate || null);
  };

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(getSettingService({ key_list: FIELD_LIST }));
      const nextForm = buildFormState(response?.data?.data || {});
      setForm(nextForm);

      if (String(nextForm.shimbirr_auth_enabled || '0') === '1') {
        await fetchPairingBundle(true);
      } else {
        setPairingBundle(null);
        setPairingGate(null);
      }
    } catch ({ message, errorData }) {
      validationErrors(errorData, message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadSettings();
  }, []);

  const persistSettings = async (nextForm) => {
    setIsSaving(true);
    try {
      await dispatch(updateSettingService(nextForm));
      await loadSettings();
    } catch ({ message, errorData }) {
      validationErrors(errorData, message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnable = async () => {
    const nextForm = {
      ...form,
      shimbirr_auth_enabled: '1',
      shimbirr_portal_label:
        String(form.shimbirr_portal_label || '').trim() || 'Taban Food',
      shimbirr_auth_client_id:
        String(form.shimbirr_auth_client_id || '').trim() || 'taban-food-suite',
      shimbirr_pair_code:
        String(form.shimbirr_pair_code || '').trim() || createPairingCode(),
    };

    await persistSettings(nextForm);
  };

  const handleDisconnect = async () => {
    await persistSettings({
      ...form,
      shimbirr_auth_enabled: '0',
    });
  };

  return (
    <Card>
      <CardHeader isBorder={true}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="db-card-title">{pageTitle}</h3>
            <p className="mt-1 text-sm text-slate-500">
              Keep this page simple: enable Shimbirr, scan the QR from the mobile
              app, and disconnect it again anytime.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill tone={statusTone} text={statusText} />
            <button
              type="button"
              onClick={() => void loadSettings()}
              disabled={isLoading || isSaving}
              className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-[#156372]/15 bg-white px-4 text-sm font-semibold text-[#156372] transition hover:bg-[#f6fbfb] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </CardHeader>

      <CardBody className="space-y-6">
        <div
          className="rounded-[30px] p-6 text-white shadow-xl"
          style={{
            background: `linear-gradient(135deg, ${THEME.ink} 0%, ${THEME.primary} 54%, ${THEME.teal} 100%)`,
          }}
        >
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-white/70">
                Shimbirr Connection
              </p>
              <h1 className="mt-2 text-2xl font-semibold">
                Enable, scan, and disconnect
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-white/80">
                All the extra setup fields are hidden now. Turn Shimbirr on, let
                the QR generate, scan it from the Shimbirr app, and disconnect it
                again from this same page whenever you want.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[420px]">
              <MetricTile label="Status" value={statusText} />
              <MetricTile
                label="QR"
                value={qrReady ? 'Generated' : shimbirrEnabled ? 'Waiting' : 'Hidden'}
              />
              <MetricTile
                label="Last update"
                value={formatInstant(pairingBundle?.issuedAt)}
              />
            </div>
          </div>
        </div>

        {!shimbirrEnabled ? (
          <Card className="overflow-hidden border border-[#156372]/10">
            <CardBody className="space-y-5">
              <div className="rounded-[28px] border border-[#156372]/10 bg-[linear-gradient(145deg,#fff7e6_0%,#ffffff_48%,#eef8f9_100%)] p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#156372]/75">
                  Enable Shimbirr
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                  Generate the QR only when you need it
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
                  Shimbirr is off right now, so the pairing QR stays hidden. Press
                  the enable button and the system will prepare the secure pairing
                  code for the mobile app.
                </p>
              </div>

              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-slate-500">
                  Nothing else is shown here until you enable Shimbirr access.
                </p>
                <PermissionCheck permsission={'update_setting'}>
                  <button
                    type="button"
                    onClick={() => void handleEnable()}
                    disabled={isSaving || isLoading}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-lg px-5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"
                    style={{ backgroundColor: THEME.primary }}
                  >
                    {isSaving ? 'Enabling...' : 'Enable Shimbirr'}
                  </button>
                </PermissionCheck>
              </div>
            </CardBody>
          </Card>
        ) : qrReady ? (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[0.92fr_1.08fr]">
            <Card className="overflow-hidden border border-[#156372]/10">
              <CardBody className="space-y-5">
                <div className="rounded-[28px] border border-[#156372]/10 bg-[linear-gradient(145deg,#fff7e6_0%,#ffffff_48%,#eef8f9_100%)] p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#156372]/75">
                    QR Ready
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                    Open Shimbirr and scan
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    Open the Shimbirr app on your phone and scan this QR to finish
                    the connection for this Taban Food portal.
                  </p>
                </div>

                <div className="rounded-[30px] border border-[#156372]/10 bg-[#f6fbfb] p-4 sm:p-5">
                  <div className="mx-auto max-w-[380px] rounded-[32px] border border-slate-200 bg-white p-5 shadow-[0_20px_45px_rgba(21,99,114,0.12)] sm:p-6">
                    <div className="rounded-[28px] border border-[#e7f2f4] bg-white p-4 sm:p-5">
                      <QRCode
                        value={pairingBundle.qrText}
                        size={QR_CARD_SIZE}
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                      />
                    </div>
                  </div>
                  <p className="mt-4 text-center text-sm leading-6 text-slate-500">
                    Bigger quiet border, stronger contrast, and a cleaner card make
                    the code easier for mobile cameras to lock onto.
                  </p>
                </div>

                <div className="rounded-2xl border border-[#156372]/10 bg-[#f6fbfb] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#156372]/75">
                    Need to stop it later?
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Press disconnect and the QR will be hidden again right away.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setIsQrOpen(true)}
                    className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-[#156372]/15 bg-[#f6fbfb] px-4 text-sm font-semibold text-[#156372] transition hover:bg-white"
                  >
                    Open Large QR
                  </button>
                  <PermissionCheck permsission={'update_setting'}>
                    <button
                      type="button"
                      onClick={() => void handleDisconnect()}
                      disabled={isSaving || isLoading}
                      className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isSaving ? 'Disconnecting...' : 'Disconnect Shimbirr'}
                    </button>
                  </PermissionCheck>
                  <button
                    type="button"
                    onClick={() => void fetchPairingBundle(true)}
                    disabled={isLoading || isSaving}
                    className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-[#156372]/15 bg-white px-4 text-sm font-semibold text-[#156372] transition hover:bg-[#f6fbfb] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    Refresh QR
                  </button>
                </div>
              </CardBody>
            </Card>

            <Card className="overflow-hidden border border-[#156372]/10">
              <CardBody className="space-y-4">
                <div className="rounded-2xl border border-[#156372]/10 bg-[#f6fbfb] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#156372]/75">
                    Scan Guide
                  </p>
                  <p className="mt-2 text-sm leading-7 text-slate-600">
                    The secure QR is live and ready for the Shimbirr app. Use these
                    quick steps to get a smoother scan on mobile.
                  </p>
                </div>

                <div className="grid gap-3">
                  <ScanStep number="1" text="Open the Shimbirr app on the phone that will connect to this Taban Food workspace." />
                  <ScanStep number="2" text="Hold the phone upright and keep the QR fully visible on the screen with no browser zoom over it." />
                  <ScanStep number="3" text="If the code feels small, use Open Large QR so the scan surface fills more of the screen." />
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-[#156372]/10 bg-white px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#156372]/70">
                      Portal
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">
                      {form.shimbirr_portal_label || 'Taban Food'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#156372]/10 bg-white px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#156372]/70">
                      Expires
                    </p>
                    <p className="mt-2 text-base font-semibold text-slate-900">
                      {formatInstant(pairingBundle?.expiresAt)}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-[#156372]/10 bg-white px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#156372]/70">
                    Trusted audience
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {(pairingBundle?.audience || []).join(', ') ||
                      'Shimbirr mobile and approved Taban systems'}
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : (
          <Card className="overflow-hidden border border-[#156372]/10">
            <CardBody className="space-y-5">
              <div className="rounded-[28px] bg-[linear-gradient(135deg,#0b3a3f_0%,#156372_52%,#167E7E_100%)] p-6 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/75">
                  Preparing QR
                </p>
                <h2 className="mt-2 text-2xl font-semibold">
                  Shimbirr is enabled. Waiting for the QR to generate.
                </h2>
                <p className="mt-3 text-sm leading-7 text-white/80">
                  {pairingGate?.message ||
                    'The trust checks are still running. Refresh again in a moment.'}
                </p>
              </div>

              {pairingGate?.checks?.length ? (
                <div className="grid gap-3">
                  {pairingGate.checks.map((check) => (
                    <div
                      key={check.key}
                      className="rounded-2xl border border-[#156372]/10 bg-white px-4 py-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-900">
                          {check.label}
                        </p>
                        <StatusPill
                          tone={check.ok ? 'emerald' : 'amber'}
                          text={check.ok ? 'Healthy' : 'Waiting'}
                        />
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-500">
                        {check.ok ? 'Ready.' : check.message}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void fetchPairingBundle(true)}
                  disabled={isLoading || isSaving}
                  className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-[#156372]/15 bg-white px-4 text-sm font-semibold text-[#156372] transition hover:bg-[#f6fbfb] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading ? 'Refreshing...' : 'Refresh'}
                </button>
                <PermissionCheck permsission={'update_setting'}>
                  <button
                    type="button"
                    onClick={() => void handleDisconnect()}
                    disabled={isSaving || isLoading}
                    className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSaving ? 'Disconnecting...' : 'Disconnect Shimbirr'}
                  </button>
                </PermissionCheck>
              </div>
            </CardBody>
          </Card>
        )}
      </CardBody>
      <ExpandedQrModal
        isOpen={isQrOpen}
        onClose={() => setIsQrOpen(false)}
        qrText={pairingBundle?.qrText}
        portalLabel={form.shimbirr_portal_label || 'Taban Food'}
        expiresAt={pairingBundle?.expiresAt}
      />
    </Card>
  );
};

export default ShimbirrAuthSettingsPage;
